import { defineCopy } from '@floorfile/i18n'

/**
 * Attachments: the gallery, the photo annotator and the signature pad
 * (localization plan Phase 5a).
 *
 * ## `context` is keyed by the contract enum and shared by two components
 *
 * `AttachmentContext` reached the screen twice as its raw value: as a thumbnail
 * BADGE (`FAILURE_EVIDENCE` in screaming caps over a photo) and as the option
 * text in the annotator's picker, where it was hand-written in English a second
 * time. One keyed map serves both, and adding a context to the contract now
 * fails the build here instead of shipping a constant to the floor.
 *
 * ## The two plural leaves are the first in the codebase
 *
 * Both were hand-rolled English: `photo${n === 1 ? '' : 's'}` and
 * `mark${n === 1 ? '' : 's'}`. That construction has exactly two forms and puts
 * the suffix at the end, which is a fact about English — French agrees by
 * accident, Polish needs three forms, and Japanese needs none. `{ one, other }`
 * resolves through `Intl.PluralRules` for the active locale instead, and a
 * locale that asks for a category the record lacks falls back to `other`.
 */
export const attachmentsCopy = defineCopy('attachments', {
  offlineAction: { upload: 'Upload {fileName}' },
  gallery: {
    allPhotos: 'All photos',
    /** The lightbox action on an image the reader may draw on (F7). */
    annotate: 'Annotate',
    /** Bytes not yet arrived — a gallery showing 4 of 6 reads as complete. */
    loadingPhotos: {
      one: 'Loading {count} more photo…',
      other: 'Loading {count} more photos…',
    },
    loadingFile: 'Loading {fileName}…',
    /** A derivative with no evidence context of its own. */
    annotated: 'Annotated',
    /**
     * A derivative that DOES have one. Not `${context} + ' · annotated'`:
     * concatenating a translated label with an English word produces a
     * half-French string and fixes the word order in a language that may not
     * allow it. The separator is the translator's too.
     */
    annotatedContext: '{context} · annotated',
    /** Alt text: the filename plus what the photo is evidence OF. */
    altWithLabel: '{label}: {fileName}',
  },

  /** Shared by the gallery's thumbnail badge and the annotator's picker. */
  context: {
    GENERAL: 'General',
    BEFORE: 'Before',
    AFTER: 'After',
    FAILURE_EVIDENCE: 'Failure evidence',
    REFERENCE: 'Reference',
  },

  /**
   * The SHORT form, for the four-up segmented control the work-order
   * completion dialog uses to pick what the next capture is (localization
   * chunk 5a-4). `Failure evidence` and `General` do not fit that control and
   * were written there as `Failure` and `Other` — a second English copy of
   * this enum, in another slice.
   *
   * Kept as a second register here rather than reused from `context`, because
   * collapsing them would REWORD the control, and rather than left in
   * `maintenance/copy.ts`, because the enum is this slice's: `REFERENCE` is
   * absent on purpose — a reference photo belongs to a plan step, not to a
   * completion.
   */
  contextShort: {
    GENERAL: 'Other',
    BEFORE: 'Before',
    AFTER: 'After',
    FAILURE_EVIDENCE: 'Failure',
  },
  /** The same four INSIDE "Add {x} photo" — not `.toLowerCase()` on a label. */
  contextShortInline: {
    GENERAL: 'other',
    BEFORE: 'before',
    AFTER: 'after',
    FAILURE_EVIDENCE: 'failure',
  },

  /**
   * The dictate button on `DictationTextarea`.
   *
   * Found by reading the running app in French, not by the linter: all three
   * live in ternaries (`listening ? 'Stop dictating' : 'Dictate'`), which the
   * copy rule cannot see. This file was NOT in `copy-baseline.json` — the
   * slice was already marked migrated — so nothing flagged them. **A finished
   * slice can still hold untranslated strings.**
   */
  dictation: {
    start: 'Dictate',
    stop: 'Stop dictating',
    /** Spoken only — a live region, so a blind user knows the mic is hot. */
    listening: 'Dictation is on.',
  },

  /** F7: after a photo is taken — mark it up, or keep it as it is. */
  markup: {
    title: 'Photo taken',
    alt: 'The photo just taken: {fileName}',
    intro: 'Draw on it to point out the problem, or keep it as it is. Marking up saves a copy; the photo itself is kept unchanged.',
    markUp: 'Mark up',
    done: 'Done',
  },
  annotator: {
    title: 'Annotate {fileName}',
    intro: 'Saving creates a new annotated copy. The original file is kept exactly as it was.',
    toolLegend: 'Drawing tool',
    colourLegend: 'Colour',
    tool: {
      pen: 'Pen',
      arrow: 'Arrow',
      rect: 'Rectangle',
      ellipse: 'Circle',
      text: 'Text label',
    },
    /** Ink colours. The VALUES are hex and stay in the component — they are
     * flattened into the PNG — but the names are read aloud by screen readers
     * and are words like any other. */
    colour: {
      red: 'Red',
      amber: 'Amber',
      yellow: 'Yellow',
      green: 'Green',
      blue: 'Blue',
      purple: 'Purple',
      white: 'White',
      black: 'Black',
    },
    stroke: 'Stroke',
    strokeThin: 'Thin',
    strokeMedium: 'Medium',
    strokeThick: 'Thick',
    evidenceContext: 'Evidence context',
    labelText: 'Label text',
    labelPlaceholder: 'e.g. cracked weld',
    labelHelp: 'Type the text, then tap the photo where it should sit.',
    loadFailed: 'That image could not be loaded, so it cannot be annotated.',
    loading: 'Loading the photo…',
    nothingDrawn: 'Nothing drawn yet.',
    marks: {
      one: '{count} mark — saving flattens it into a new copy.',
      other: '{count} marks — saving flattens them into a new copy.',
    },
    undo: 'Undo',
    /** F7 */
    redo: 'Redo',
    reset: 'Clear all',
    clearTitle: 'Clear every mark?',
    clearBody: {
      one: 'The mark on this photo will be removed. Undo cannot bring it back.',
      other: 'All {count} marks on this photo will be removed. Undo cannot bring them back.',
    },
    labelFirst: 'Type the label text first, then tap where it goes.',
    renderFailed: 'The annotated image could not be rendered.',
    saveFailed: 'The annotation could not be saved.',
    cancel: 'Cancel',
    save: 'Save annotated copy',
    saving: 'Saving…',
    locationDiscarded: 'Location data is not carried into the copy — redrawing the pixels discards it.',
  },

  /**
   * F32: the specific reason an upload was refused — checked before upload
   * and read back from a server 422's field details. Never the server's
   * English, never the bare "One or more fields are invalid."
   */
  upload: {
    empty: 'This file is empty.',
    tooLarge: 'This file is larger than {max} MB.',
    nameTooLong: 'The file name is too long ({max} characters max).',
    typeNotAllowed: 'This file type isn’t allowed: {type}.',
    typeNotAllowedGeneric: 'This file type isn’t allowed.',
    failed: 'Upload failed.',
    attach: 'Attach a file',
    disabled: 'Uploads disabled',
    /** F5.3: the camera-first pair on a touch device. */
    takePhoto: 'Take photo',
    chooseFile: 'Choose file',
  },

  signature: {
    signed: '✓ Signed',
    clearAndResign: 'Clear & re-sign',
    confirm: 'Confirm signature',
    clear: 'Clear',
    /** Canvas → PNG conversion failed before any request was made. */
    readFailed: 'Could not read the signature.',
    uploadFailed: 'Signature upload failed.',
  },
})

export type AttachmentsCopy = typeof attachmentsCopy.en
